/**
 * src/pages/FallacyLibrary/sections/FallacyCard.tsx
 * Reusable full-depth entry component for all 54 fallacies.
 *
 * Implements all 10 required parts per spec:
 * 1. Definition
 * 2. Realistic fictional example
 * 3. Step-by-step analysis
 * 4. The exact reasoning problem
 * 5. Why it can seem persuasive
 * 6. What the fallacy does and does not establish (scope & limits, fallacy fallacy)
 * 7. Corrected or stronger version of the argument
 * 8. Counterexample or similar-looking reasonable comparison
 * 9. One-item practice question with explained feedback
 * 10. Related fallacies (#fallacy-<id>) & related glossary terms (<Term id=...>)
 */

import React, { useState } from "react";
import type { FallacyEntry } from "../types";
import { Link } from "@/components/common/Link";
import { Term } from "@/components/knowledge/Term";
import { StatusBadge } from "@/components/knowledge/StatusBadge";
import { LessonStep } from "@/components/knowledge/LessonStep";
import { Feedback } from "@/components/knowledge/Feedback";
import { ChevronDown, BookOpen, HelpCircle, Link2, Check, AlertCircle, Lightbulb, ShieldCheck, RotateCcw } from "lucide-react";
import "@/components/cosmic/cosmic.css";
import "../fallacyLibrary.css";
export interface FallacyCardProps {
  fallacy: FallacyEntry;
  isExpanded: boolean;
  onToggleExpand: () => void;
}
export function FallacyCard({
  fallacy,
  isExpanded,
  onToggleExpand
}: FallacyCardProps) {
  // Practice question state (component memory only)
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const handleSelectOption = (idx: number) => {
    setSelectedOption(idx);
    setHasAnswered(true);
  };
  const handleResetPractice = () => {
    setSelectedOption(null);
    setHasAnswered(false);
  };
  const categoryBorderMap: Record<string, string> = {
    relevance: "fl-card-relevance",
    presumption: "fl-card-presumption",
    evidence: "fl-card-evidence",
    causal: "fl-card-causal",
    language: "fl-card-language",
    formal: "fl-card-formal",
    statistical: "fl-card-statistical"
  };
  const borderClass = categoryBorderMap[fallacy.category] || "fl-card-relevance";
  return <article id={`fallacy-${fallacy.id}`} className={`ql-glass rounded-xl border border-border/60 overflow-hidden my-5 transition-all fl-scroll-target scroll-mt-[96px] ${borderClass} ${isExpanded ? "border-primary/50 shadow-[0_0_24px_rgba(0,0,0,0.5)]" : ""}`}>
      {/* Header Summary / Toggle Button */}
      <button type="button" onClick={onToggleExpand} aria-expanded={isExpanded} aria-controls={`fallacy-content-${fallacy.id}`} className="w-full flex items-start sm:items-center justify-between gap-4 p-4 sm:p-6 text-left bg-card/40 hover:bg-card/75 transition-colors cursor-pointer min-h-[56px] select-none touch-manipulation focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-1.5 min-w-0">
            <span className="fl-telemetry-tag">{fallacy.categoryLabel}</span>
            <StatusBadge status={fallacy.status} compact />
            {fallacy.aliases.length > 0 && <span className="text-xs text-muted-foreground font-sans truncate max-w-full hidden md:inline" title={`aka: ${fallacy.aliases.join(", ")}`}>
                aka: {fallacy.aliases.slice(0, 2).join(", ")}
              </span>}
          </div>

          <h3 className="font-serif text-base sm:text-xl font-bold text-foreground tracking-wide group-hover:text-primary transition-colors break-words">
            {fallacy.name}
          </h3>

          {!isExpanded && <p className="font-sans text-xs sm:text-sm text-muted-foreground line-clamp-2 mt-1 leading-relaxed break-words">
              {fallacy.definition}
            </p>}
        </div>

        <div className="flex items-center gap-2 shrink-0 pt-1 sm:pt-0">
          <span className="font-mono text-xs text-primary/80 hidden sm:inline">
            {isExpanded ? "Collapse" : "Explore"}
          </span>
          <ChevronDown className={`size-5 text-primary transition-transform duration-200 ${isExpanded ? "rotate-180" : "rotate-0"}`} aria-hidden="true" />
        </div>
      </button>

      {/* Expanded Full Depth Content */}
      {isExpanded && <div id={`fallacy-content-${fallacy.id}`} className="p-4 sm:p-7 border-t border-border/40 font-sans text-sm sm:text-base text-foreground/90 space-y-6">
          {/* Part 1: Definition */}
          <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/40 bg-card/50 min-w-0">
            <span className="fl-telemetry-tag block w-fit mb-2">1. Formal Definition</span>
            <p className="font-sans text-sm sm:text-base text-foreground leading-relaxed break-words">
              {fallacy.definition}
            </p>
          </div>

          {/* Part 2: Realistic Fictional Example */}
          <LessonStep kind="example" title="Realistic Fictional Scenario">
            <p className="italic text-foreground/95 bg-background/50 p-3.5 sm:p-4 rounded-lg border border-border/40 break-words">
              "{fallacy.example}"
            </p>
          </LessonStep>

          {/* Part 3: Step-by-Step Analysis */}
          <LessonStep kind="analysis" title="Step-by-Step Epistemic Decomposition">
            <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
              {fallacy.stepByStepAnalysis.map((step, idx) => <li key={idx} className="leading-relaxed break-words">
                  <span className="text-foreground/90">{step}</span>
                </li>)}
            </ol>
          </LessonStep>

          {/* Part 4 & 5: The Exact Reasoning Problem & Why It Seems Persuasive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/40 bg-card/40 min-w-0">
              <div className="flex items-center gap-2 mb-2 text-destructive">
                <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
                <h4 className="font-serif text-sm font-bold uppercase tracking-wider text-foreground">
                  The Exact Reasoning Problem
                </h4>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                {fallacy.reasoningProblem}
              </p>
            </div>

            <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/40 bg-card/40 min-w-0">
              <div className="flex items-center gap-2 mb-2 text-primary">
                <Lightbulb className="size-4 shrink-0" aria-hidden="true" />
                <h4 className="font-serif text-sm font-bold uppercase tracking-wider text-foreground">
                  Why It Can Seem Persuasive
                </h4>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
                {fallacy.whyPersuasive}
              </p>
            </div>
          </div>

          {/* Part 6: What the Fallacy Does & Does Not Establish (Scope & Limits) */}
          <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/40 bg-card/40 min-w-0">
            <div className="flex items-center gap-2 mb-2 text-amber-400">
              <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
              <h4 className="font-serif text-sm font-bold uppercase tracking-wider text-foreground">
                What This Fallacy Does and Does Not Establish
              </h4>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
              {fallacy.scopeAndLimits}
            </p>
          </div>

          {/* Part 7: Corrected or Stronger Version of the Argument */}
          <LessonStep kind="application" title="Corrected / Legitimate Argument">
            <p className="text-foreground/90 font-mono text-xs sm:text-sm bg-background/60 p-3.5 rounded-lg border border-border/40 leading-relaxed break-words">
              {fallacy.correctedVersion}
            </p>
          </LessonStep>

          {/* Part 8: Counterexample or Reasonable Comparison */}
          <div className="ql-card p-4 sm:p-5 rounded-lg border border-border/40 bg-card/40 min-w-0">
            <h4 className="font-serif text-sm font-bold uppercase tracking-wider text-foreground mb-2 flex items-center gap-2">
              <BookOpen className="size-4 text-primary shrink-0" aria-hidden="true" />
              <span>Similar-Looking Reasonable Argument (Comparison)</span>
            </h4>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
              {fallacy.reasonableComparison}
            </p>
          </div>

          {/* Part 9: One-Item Practice Question with Explained Feedback */}
          <div className="ql-glass p-4 sm:p-6 rounded-xl border border-border/60 bg-card/60 my-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <HelpCircle className="size-4 text-primary shrink-0" aria-hidden="true" />
                <h4 className="font-serif text-sm sm:text-base font-bold text-foreground">
                  Epistemic Practice Check
                </h4>
              </div>
              {hasAnswered && <button type="button" onClick={handleResetPractice} className="inline-flex items-center gap-1.5 text-xs font-mono text-primary hover:underline cursor-pointer">
                  <RotateCcw className="size-3" aria-hidden="true" />
                  <span>Retry Question</span>
                </button>}
            </div>

            <p className="font-sans text-xs sm:text-sm text-foreground/90 mb-4 leading-relaxed">
              {fallacy.practice.prompt}
            </p>

            <div className="space-y-2 mb-4">
              {fallacy.practice.options.map((opt, oIdx) => {
            const isSelected = selectedOption === oIdx;
            const isCorrect = oIdx === fallacy.practice.correctIndex;
            let optionStyle = "border-border/60 bg-card/30 hover:border-primary/50 text-foreground/90";
            if (hasAnswered) {
              if (isSelected && isCorrect) {
                optionStyle = "border-emerald-500 bg-emerald-950/30 text-emerald-200";
              } else if (isSelected && !isCorrect) {
                optionStyle = "border-destructive bg-destructive/10 text-destructive-foreground";
              } else if (isCorrect) {
                optionStyle = "border-emerald-500/60 bg-emerald-950/20 text-emerald-300";
              } else {
                optionStyle = "opacity-50 border-border/30";
              }
            }
            return <button key={oIdx} type="button" onClick={() => !hasAnswered && handleSelectOption(oIdx)} disabled={hasAnswered} className={`w-full text-left p-3.5 rounded-lg border font-sans text-xs sm:text-sm transition-all flex items-start justify-between gap-3 min-h-[48px] cursor-pointer touch-manipulation focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 break-words ${optionStyle}`}>
                    <span className="flex-1 break-words">{opt}</span>
                    {hasAnswered && isCorrect && <Check className="size-4 text-emerald-400 shrink-0 mt-0.5" aria-hidden="true" />}
                  </button>;
          })}
            </div>

            {hasAnswered && <Feedback tone={selectedOption === fallacy.practice.correctIndex ? "good" : "partial"} title={selectedOption === fallacy.practice.correctIndex ? "Sound Epistemic Classification" : "Nuanced Analysis"}>
                {fallacy.practice.explanation}
              </Feedback>}
          </div>

          {/* Part 10: Related Fallacies & Related Glossary Terms */}
          <div className="border-t border-border/40 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs min-w-0">
            {/* Related on-page fallacies */}
            {fallacy.relatedFallacyIds.length > 0 && <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="font-mono text-muted-foreground shrink-0">Related Fallacies:</span>
                {fallacy.relatedFallacyIds.map(relId => <Link key={relId} to={`#fallacy-${relId}`} className="inline-flex items-center gap-1 font-mono text-primary hover:underline bg-card/60 px-2 py-0.5 rounded border border-border/40 break-words max-w-full">
                    <Link2 className="size-3 shrink-0" aria-hidden="true" />
                    <span className="break-all">{relId}</span>
                  </Link>)}
              </div>}

            {/* Related glossary terms */}
            {fallacy.relatedTermIds.length > 0 && <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="font-mono text-muted-foreground shrink-0">Glossary Terms:</span>
                {fallacy.relatedTermIds.map(termId => <Term key={termId} id={termId} />)}
              </div>}
          </div>
        </div>}
    </article>;
}